import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Raffle } from "@/campaigns/modules/raffle/types";
import {
  drawDate,
  rafflePhase,
  viewerWinnings,
} from "@/campaigns/modules/raffle/types";
import { PrizeGamesChips } from "@/components/campaigns/prize-games-chips";
import { PrizeText } from "@/components/prize-text";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useViewerHash } from "@/hooks/use-viewer-hash";
import { cn } from "@/lib/utils";
import { formatPrizeTitle } from "@/utils/helpers";

import { DrawCountdown } from "./draw-countdown";
import { formatDrawDate } from "./format";
import { RafflePrizes } from "./raffle-prizes-card";

const LABEL =
  "font-body text-[13px] leading-[18px] text-muted-foreground lg:text-[14px] lg:leading-5";
const NOTE =
  "font-body text-[13px] leading-[18px] text-muted-foreground tabular-nums lg:text-[15px] lg:leading-5";
const HEADLINE =
  "font-heading text-[32px] leading-[1.1] font-extrabold lg:text-[44px]";

/** Open: the player's ticket balance beside the ticking time to the draw. The balance is only as
 *  fresh as the `raffle_progress` stream (a few seconds behind play on legacy-transport casinos),
 *  so it is a plain number — no animation that implies sub-second accuracy. */
const LiveSummary = ({
  raffle,
  joined,
}: {
  raffle: Raffle;
  joined: boolean;
}) => {
  const { t } = useTranslation();
  const tickets = raffle.total_tickets;
  const draw = drawDate(raffle);
  const drawLabel = formatDrawDate(draw);
  const note = !joined
    ? t("raffle.optInToEarn", "Opt in to start earning tickets")
    : tickets === 0
      ? t("raffle.noTicketsYet", "Complete a task to earn your first ticket")
      : tickets !== null
        ? t("raffle.ticketsInDraw", "{{count}} tickets in the draw", {
            count: tickets,
          })
        : null;
  return (
    // Side by side while the card is wide enough for the countdown; stacked in the narrow in-game
    // column, where it would be clipped.
    <div className="grid grid-cols-1 gap-4 @[22rem]:grid-cols-2 @[22rem]:gap-x-4 @[22rem]:gap-y-0 lg:@[22rem]:gap-x-6">
      <div className="flex min-w-0 flex-col gap-1.5">
        <span className={LABEL}>{t("raffle.yourTickets", "Your tickets")}</span>
        {joined &&
          (tickets === null ? (
            <Skeleton className="h-10 w-16 bg-background-card-surface" />
          ) : (
            // `font-heading` is explicit: the figure sits in a body-font context and Geist has
            // no 800 weight loaded (same reason as the Sprint Mission winners count).
            <span
              role="heading"
              aria-level={3}
              className="font-heading text-[48px] leading-none font-extrabold text-prizes tabular-nums lg:text-[64px] ls:text-[40px]"
            >
              {tickets}
            </span>
          ))}
        {note && <p className={NOTE}>{note}</p>}
      </div>
      <div className="flex min-w-0 flex-col gap-1.5 border-t border-foreground/[0.08] pt-4 @[22rem]:border-t-0 @[22rem]:border-l @[22rem]:pt-0 @[22rem]:pl-4 lg:@[22rem]:pl-6">
        <span className={LABEL}>{t("raffle.drawIn", "Draw in")}</span>
        <span className="font-heading text-[28px] leading-none font-extrabold text-foreground lg:text-[40px] ls:text-[28px]">
          <DrawCountdown date={draw} />
        </span>
        {drawLabel && <p className={NOTE}>{drawLabel}</p>}
      </div>
    </div>
  );
};

/** The draw's own header: "Draw result" at the start, when it ended at the far end. */
const DrawResultHeader = ({ ended }: { ended?: string }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
      <span className={LABEL}>{t("raffle.drawResult", "Draw result")}</span>
      {ended && (
        <span className={cn(LABEL, "text-right")}>
          {t("raffle.drawEnded", "Draw ended · {{date}}", { date: ended })}
        </span>
      )}
    </div>
  );
};

/** Entries are closed and nobody has won yet. No countdown: the draw is imminent, and a clock at
 *  zero would read as a bug. */
const AwaitingSummary = ({
  raffle,
  joined,
}: {
  raffle: Raffle;
  joined: boolean;
}) => {
  const { t } = useTranslation();
  const tickets = raffle.total_tickets;
  return (
    <div className="flex flex-col gap-2">
      <DrawResultHeader />
      <h3 className={cn(HEADLINE, "text-foreground")}>
        {t("raffle.entriesClosed", "Entries closed")}
      </h3>
      <p className={NOTE}>
        {t(
          "raffle.awaitingDraw",
          "The draw is coming up. Winners will appear here."
        )}
      </p>
      {joined && tickets !== null && tickets > 0 && (
        <p className={NOTE}>
          {t("raffle.ticketsInDraw", "{{count}} tickets in the draw", {
            count: tickets,
          })}
        </p>
      )}
    </div>
  );
};

/** Drawn: the reader's own result leads — every prize they won, whole (each shrinks, then glides
 *  on its one row), or the plain "no win". The other winners are listed below. */
const DrawnSummary = ({ raffle }: { raffle: Raffle }) => {
  const { t } = useTranslation();
  const viewerHash = useViewerHash();
  const won = viewerWinnings(raffle, viewerHash);
  const tickets = raffle.total_tickets;
  return (
    <div className="flex flex-col gap-3">
      <DrawResultHeader ended={formatDrawDate(drawDate(raffle))} />
      {won.length > 0 ? (
        <>
          <h3 className={cn(HEADLINE, "text-prizes")}>
            {t("raffle.youWon", "You won {{count}} prizes!", {
              count: won.length,
            })}
          </h3>
          {/* One column until the card is wide enough for two whole prize titles (the narrow in-game column clipped them). */}
          <ul className="grid grid-cols-1 gap-2 @[30rem]:grid-cols-2">
            {won.map((w, i) => (
              <li
                key={`${w.prize_title}-${i}`}
                className="glass-card glass-card-won flex min-w-0 items-center gap-3 rounded-(--border-radius-inner) px-3.5 py-3"
              >
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-prizes text-background">
                  <Check className="size-4" strokeWidth={3} aria-hidden />
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <PrizeText
                    size="inherit"
                    weight="bold"
                    tone="flat"
                    shadow={false}
                    fit="marquee"
                    className="text-[16px] leading-5 text-foreground lg:text-[18px] lg:leading-6"
                  >
                    {formatPrizeTitle(w.prize_title, t)}
                  </PrizeText>
                  <PrizeGamesChips
                    games={
                      raffle.prizes.find(
                        (p) =>
                          p.place_from === w.place_from &&
                          p.place_to === w.place_to
                      )?.games ?? []
                    }
                  />
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <h3 className={cn(HEADLINE, "text-foreground")}>
            {t("raffle.noWin", "No win this time")}
          </h3>
          {tickets !== null && tickets > 0 && (
            <p className={cn(NOTE, "lg:text-[17px] lg:leading-6")}>
              {t(
                "raffle.noWinBody",
                "Your {{count}} tickets were in the draw, but no winners. Better luck next time!",
                { count: tickets }
              )}
            </p>
          )}
        </>
      )}
    </div>
  );
};

/**
 * The card that tops the main column, in the raffle's three visibly different states
 * (`rafflePhase`), with the prize ladder under it: open (tickets + time to the draw), awaiting the
 * draw (entries closed), drawn (the reader's result). `prizes={false}` leaves the ladder out for
 * the sideways-phone pane, which lists it in its own card.
 */
export const RaffleOverviewCard = ({
  raffle,
  joined,
  prizes = true,
  className,
}: {
  raffle: Raffle;
  joined: boolean;
  prizes?: boolean;
  className?: string;
}) => {
  const phase = rafflePhase(raffle);
  return (
    <Card
      data-phase={phase}
      className={cn("@container flex flex-col gap-4 lg:gap-5", className)}
    >
      {phase === "live" ? (
        <LiveSummary raffle={raffle} joined={joined} />
      ) : phase === "drawn" ? (
        <DrawnSummary raffle={raffle} />
      ) : (
        <AwaitingSummary raffle={raffle} joined={joined} />
      )}
      {prizes && raffle.prizes.length > 0 && (
        <>
          <div className="h-px w-full bg-foreground/[0.08]" />
          <RafflePrizes raffle={raffle} />
        </>
      )}
    </Card>
  );
};
